import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { EVENT_KEY_ESCAPE } from '../../../../constants/event'
import {
  HTML_DATA_PARAM_FILE_PREVIEW_OPEN,
  HTML_ELEMENT_TAB_INDEX_NOT_TABABLE,
} from '../../../../constants/html'
import { SIZE } from '../../../../constants/size'
import { useKeyboardListener } from '../../../../hooks'
import { FormActions, FormModalConfirmation } from '../../../Form'
import Modal, { Box, Content, Header } from '../../../Modal'
import { RentalPortalPhotoGallery } from '../../RentalPortalPhotoGallery'

import { QA_NAME_LISTING_PHOTO_EDITOR_MODAL } from './RentalPortalListingProfilePhotoEditorModal.constants'
import { useListingProfilePhotoEditorModal } from './RentalPortalListingProfilePhotoEditorModal.hooks'
import {
  getHasAbandonedListingPhotoUploads,
  getHasListingPhotoUploadsInProgress,
} from './RentalPortalListingProfilePhotoEditorModal.utils'

import * as styles from './RentalPortalListingProfilePhotoEditorModal.module.css'

function RentalPortalListingProfilePhotoEditorModal({
  initialPhotos,
  listingId,
  onClose,
  onReload,
  onSave,
}) {
  const { t } = useTranslation()

  const boxRef = useRef(null)

  const {
    handleCancelClose,
    handleChangePhotos,
    handleClose,
    handleConfirmClose,
    handleDeletePhoto,
    handleSubmit,
    isCloseConfirm,
    isSubmitting,
    photos,
    setUploadingFiles,
    uploadingFiles,
  } = useListingProfilePhotoEditorModal({
    initialPhotos,
    listingId,
    onClose,
    onReload,
    onSave,
  })

  function handleEscape() {
    if (isCloseConfirm) {
      handleCancelClose()
    } else if (!document.body.hasAttribute(HTML_DATA_PARAM_FILE_PREVIEW_OPEN)) {
      handleClose()
    }
  }

  useKeyboardListener({
    callback: handleEscape,
    dependencies: [handleCancelClose, handleClose, isCloseConfirm],
    eventKey: EVENT_KEY_ESCAPE,
  })

  useEffect(() => {
    boxRef.current?.focus()
  }, [])

  return (
    <>
      <Modal qaName={QA_NAME_LISTING_PHOTO_EDITOR_MODAL} onClose={handleClose}>
        <Box
          ref={boxRef}
          size={SIZE.LARGE}
          tabIndex={HTML_ELEMENT_TAB_INDEX_NOT_TABABLE}
        >
          <Header
            text={t('RentalPortal.ListingPhotoEditorTitleKey')}
            onClose={isSubmitting ? undefined : handleClose}
          />
          <Content size={SIZE.LARGE}>
            <RentalPortalPhotoGallery
              disabled={isSubmitting}
              photos={photos}
              onChange={handleChangePhotos}
              onDeletePhoto={handleDeletePhoto}
              onUploadingFilesChange={setUploadingFiles}
            />
          </Content>
          <FormActions
            ButtonAdd={false}
            ButtonClear={false}
            UploadComponent={false}
            cancelProps={{ disabled: isSubmitting, onClick: handleClose }}
            className={styles.actions}
            isSubmitting={isSubmitting}
            submitProps={{
              disabled: getHasListingPhotoUploadsInProgress(uploadingFiles),
              isLoading: isSubmitting,
              title: 'Modal.SaveBtnKey',
              tooltip: getHasListingPhotoUploadsInProgress(uploadingFiles)
                ? t('Form.CommonUploadFilesWarningKey')
                : undefined,
              onClick: handleSubmit,
            }}
          />
        </Box>
      </Modal>
      {isCloseConfirm ? (
        <FormModalConfirmation
          {...(getHasAbandonedListingPhotoUploads({ photos, uploadingFiles })
            ? { description: 'RentalPortal.ListingFormAbandonedUploadsKey' }
            : null)}
          onCancel={handleCancelClose}
          onSubmit={handleConfirmClose}
        />
      ) : null}
    </>
  )
}

export default RentalPortalListingProfilePhotoEditorModal
